import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import {
  DE_SLOPING_MAP,
  DEBUNKERING_MAP,
  SHIP_TO_SHIP,
} from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import { useTranslation } from "react-i18next";
import { usePortServicesFlow } from "../context/PortServicesFlowContext";
import { isShipToShipType } from "../ship-to-ship/utils";

const LANG_BASE = "vessel-call.port-services";
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;
const ACTUAL_QUANTITY_MAX_DIGITS = 15;

interface ActualQuantityFieldProps {
  serviceDetails: {
    quantity: string | number | undefined;
    serviceCategory?: string;
    serviceType?: string;
    unitOfMeasurement?: string;
  };
}

const ActualQuantityField = ({
  serviceDetails: { quantity: currentQuantity, serviceCategory, serviceType, unitOfMeasurement },
}: ActualQuantityFieldProps) => {
  const { setActualQuantity, errors, setErrors } = usePortServicesFlow();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const isShipToShip = isShipToShipType(serviceCategory || "", serviceType || "");
  const label = isShipToShip
    ? t(`${LANG_BASE}.fields.quantity-tons`)
    : t(`${LANG_BASE}.fields.actual-quantity`);
  useEffect(() => {
    const parsedQuantity =
      typeof currentQuantity === "string"
        ? currentQuantity.trim() === ""
          ? undefined
          : Number(currentQuantity)
        : currentQuantity;

    const validQuantity =
      parsedQuantity == null ||
      Number.isNaN(parsedQuantity as number) ||
      !Number.isInteger(parsedQuantity) ||
      parsedQuantity <= 0
        ? undefined
        : parsedQuantity;

    setActualQuantity(validQuantity);
    setInputValue(validQuantity != null ? String(validQuantity) : "");
  }, [currentQuantity, setActualQuantity]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setInputValue("");
      setActualQuantity(undefined);
      if (errors.actualQuantity) {
        setErrors({
          ...errors,
          actualQuantity: undefined,
        });
      }
      return;
    }

    if (!POSITIVE_INTEGER_PATTERN.test(value)) return;

    if (value.length > ACTUAL_QUANTITY_MAX_DIGITS) {
      setErrors({
        ...errors,
        actualQuantity: t("form-validation.max-length-digits", {
          label,
          maxDigits: ACTUAL_QUANTITY_MAX_DIGITS,
        }),
      });
      return;
    }

    const numberValue = Number(value);

    setInputValue(value);
    setActualQuantity(Number.isNaN(numberValue) ? undefined : numberValue);

    if (errors.actualQuantity) {
      setErrors({
        ...errors,
        actualQuantity: undefined,
      });
    }
  };

  const handleBlur = () => {
    if (!errors.actualQuantity) return;

    setErrors({
      ...errors,
      actualQuantity: undefined,
    });
  };

  const getFieldLabel = () => {
    if (
      unitOfMeasurement &&
      serviceCategory &&
      [DEBUNKERING_MAP.CATEGORY, DE_SLOPING_MAP.CATEGORY].includes(serviceCategory)
    ) {
      return `${t(`${LANG_BASE}.fields.actual-quantity`)} (${unitOfMeasurement})`;
    }
    if (serviceCategory === SHIP_TO_SHIP.LABEL) {
      return t(`${LANG_BASE}.fields.quantity-tons`);
    }
    return t(`${LANG_BASE}.fields.actual-quantity`);
  };

  return (
    <div className="flex flex-col gap-2">
      <CustomFormLabel data-testid="actualQuantity-label">{getFieldLabel()}</CustomFormLabel>
      <Input
        type="text"
        inputMode="numeric"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        testId={isShipToShip ? "quantity-input" : "actual-quantity-input"}
        sx={{ width: "100%" }}
        placeholder={getFieldLabel()}
        fullWidth
      />
      {errors.actualQuantity && (
        <Typography variant="caption" color="error" sx={{ display: "block" }}>
          {errors.actualQuantity}
        </Typography>
      )}
    </div>
  );
};

export default ActualQuantityField;
